import {
  DEFAULT_NAMESPACE,
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { getAnnotation } from './annotations';

export const ANNOTATION_CONNECTIONS = 'aurum.com.br/connections';

export interface C4Connection {
  target: string;
  via?: string;
  technology?: string;
  description?: string;
}

const FIELD_SEPARATOR = '|';
const MAX_FIELDS = 4;

export function normalizeEntityRef(ref: string): string | undefined {
  try {
    return stringifyEntityRef(
      parseEntityRef(ref.trim(), {
        defaultKind: 'component',
        defaultNamespace: DEFAULT_NAMESPACE,
      }),
    ).toLocaleLowerCase('en-US');
  } catch {
    return undefined;
  }
}

function parseLine(line: string): C4Connection | undefined {
  const fields = line.split(FIELD_SEPARATOR, MAX_FIELDS).map(f => f.trim());
  const target = fields[0] ? normalizeEntityRef(fields[0]) : undefined;
  if (!target) {
    return undefined;
  }

  return {
    target,
    ...(fields[1] ? { via: fields[1] } : {}),
    ...(fields[2] ? { technology: fields[2] } : {}),
    ...(fields[3] ? { description: fields[3] } : {}),
  };
}

export function parseConnections(entity: Entity | undefined): C4Connection[] {
  const raw = entity
    ? getAnnotation(entity, ANNOTATION_CONNECTIONS)
    : undefined;
  if (!raw) {
    return [];
  }

  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith('#'))
    .map(parseLine)
    .filter((connection): connection is C4Connection => Boolean(connection));
}

export function findConnections(
  source: Entity | undefined,
  targetRef: string | undefined,
): C4Connection[] {
  if (!source || !targetRef) {
    return [];
  }

  const normalized = normalizeEntityRef(targetRef);
  if (!normalized) {
    return [];
  }

  return parseConnections(source).filter(c => c.target === normalized);
}

export function findConnection(
  source: Entity | undefined,
  targetRef: string | undefined,
): C4Connection | undefined {
  return findConnections(source, targetRef)[0];
}
